import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import BarcodeScanner from '../components/BarcodeScanner';
import { parseGS1 } from '../utils/gs1Parser';
import { useUser } from '../context/UserContext';
import toast from 'react-hot-toast';
import { Database, Check, X, PackagePlus } from 'lucide-react';

// Pour s'assurer que Tailwind compile bien les classes générées dynamiquement
const TAILWIND_SAFELIST = [
  'bg-red-50', 'hover:bg-red-100', 'border-red-400', 'text-red-800', 'focus:border-red-600', 'border-red-100', 'bg-red-200', 'border-red-200', 'border-red-300', 'focus:border-red-500', 'text-red-600',
  'bg-blue-50', 'hover:bg-blue-100', 'border-blue-400', 'text-blue-800', 'focus:border-blue-600', 'border-blue-100', 'bg-blue-200', 'border-blue-200', 'border-blue-300', 'focus:border-blue-500', 'text-blue-600',
  'bg-emerald-50', 'hover:bg-emerald-100', 'border-emerald-400', 'text-emerald-800', 'focus:border-emerald-600', 'border-emerald-100', 'bg-emerald-200', 'border-emerald-200', 'border-emerald-300', 'focus:border-emerald-500', 'text-emerald-600'
];

let globalMedicamentsCache = null;

const fetchJSONP = (url) => {
  return new Promise((resolve, reject) => {
    const callbackName = 'jsonp_callback_' + Math.round(100000 * Math.random());
    window[callbackName] = function(data) {
      delete window[callbackName];
      document.body.removeChild(script);
      resolve(data);
    };
    const script = document.createElement('script');
    script.src = url + (url.includes('?') ? '&' : '?') + 'callback=' + callbackName;
    script.onerror = function() {
      delete window[callbackName];
      document.body.removeChild(script);
      reject(new Error('JSONP request failed'));
    };
    document.body.appendChild(script);
  });
};

export default function Scanner() {
  const navigate = useNavigate();
  const { etablissement } = useUser();
  const trigrammeEtab = etablissement?.etablissement_trigramme || etablissement?.trigramme || 'AUT';

  const [isScannerOpen, setIsScannerOpen] = useState(false);
  
  const [scannedMed, setScannedMed] = useState(null);
  const [articleInfo, setArticleInfo] = useState(null); 
  const [selectedEmp, setSelectedEmp] = useState(''); 
  const [newEmpAutre, setNewEmpAutre] = useState(''); 
  
  const [modalType, setModalType] = useState('consume'); 
  const [modalStep, setModalStep] = useState(0); 

  const [selectedQty, setSelectedQty] = useState(1);
  const [showCustomQty, setShowCustomQty] = useState(false);
  const [customQtyValue, setCustomQtyValue] = useState('');
  
  const [isUnknownMed, setIsUnknownMed] = useState(false);
  const [newProcessus, setNewProcessus] = useState('');
  const [newProcessusAutre, setNewProcessusAutre] = useState('');
  const [newForme, setNewForme] = useState('');
  const [newUnite, setNewUnite] = useState('');
  const [newUniteAutre, setNewUniteAutre] = useState('');

  const [restockData, setRestockData] = useState(null); 
  const [restockAddedQty, setRestockAddedQty] = useState(1);
  const [restockDeletedRows, setRestockDeletedRows] = useState([]);

  const [medicamentsDB, setMedicamentsDB] = useState({});
  const [allEmplacements, setAllEmplacements] = useState([]);
  const [isDbLoaded, setIsDbLoaded] = useState(false);
  const [dbLoadError, setDbLoadError] = useState(false);

  const SCRIPT_URL = "https://script.google.com/a/macros/monurgencesante.fr/s/AKfycbxLuHgKtmXq72Y7J5X8cC8ES0V_-J0qOnzTeTFMxph8KC7liWAanybSQO9KNThyA8YK/exec";

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsScannerOpen(true);
    }, 400); 
    return () => clearTimeout(timer);
  }, []);

  const loadDatabase = useCallback(async (force = false) => {
    if (globalMedicamentsCache && !force) {
      setMedicamentsDB(globalMedicamentsCache.medicaments);
      setAllEmplacements(globalMedicamentsCache.emplacements || []);
      setIsDbLoaded(true);
      return;
    }
    setIsDbLoaded(false);
    setDbLoadError(false);
    try {
      const data = await fetchJSONP(`${SCRIPT_URL}?action=sync&site=${trigrammeEtab}&t=${Date.now()}`);
      if (data && data.medicaments) {
        globalMedicamentsCache = data;
        setMedicamentsDB(data.medicaments);
        setAllEmplacements(data.emplacements || []);
        setIsDbLoaded(true);
      } else {
        setDbLoadError(true);
      }
    } catch (err) {
      setDbLoadError(true);
    }
  }, [trigrammeEtab]);

  useEffect(() => {
    loadDatabase();
  }, [loadDatabase]);

  const handleScan = useCallback((codeData) => {
    setIsScannerOpen(false); 
    setModalType('consume');
    setModalStep(0);
    setCustomQtyValue('');
    setShowCustomQty(false);
    setSelectedQty(1);

    const parsedMed = parseGS1(codeData);
    
    if (parsedMed.cip !== "Introuvable") {
      setScannedMed(parsedMed);
      
      if (!isDbLoaded) {
        setArticleInfo({ found: false, error: "La base médicaments est en cours de chargement..." });
        return;
      }

      const medInfo = medicamentsDB[parsedMed.cip];
      let availableEmps = [];

      if (medInfo) {
        setIsUnknownMed(false);
        setArticleInfo({ found: true, ...medInfo });
        availableEmps = medInfo.emplacements ? medInfo.emplacements.map(e => e.emp) : [];
        setModalStep(1); 
      } else {
        setIsUnknownMed(true);
        setArticleInfo({ found: false });
        setModalType('restock');
        setNewProcessus('');
        setNewProcessusAutre('');
        setNewForme('');
        setNewUnite('');
        setNewUniteAutre('');
        availableEmps = allEmplacements;
        setModalStep('new_form');
      }

      let defaultEmp = availableEmps.length > 0 ? availableEmps[0] : '';
      if (trigrammeEtab === 'AUT') {
        defaultEmp = availableEmps.includes('1F') ? '1F' : (availableEmps.length > 0 ? availableEmps[0] : '1F');
      }
      setSelectedEmp(defaultEmp);
    } else {
      setScannedMed(parsedMed);
    }
  }, [allEmplacements, isDbLoaded, medicamentsDB, trigrammeEtab]);

  const handleOpenRestock = () => {
    setModalType('restock');
    setModalStep(1); 
    setRestockData({ dates: articleInfo?.dates || [] });
    setRestockDeletedRows([]);
    setRestockAddedQty(1);
  };

  const finalProcessus = newProcessus === 'Autre' ? newProcessusAutre : newProcessus;
  const finalUnite = newUnite === 'Autre' ? newUniteAutre : newUnite;
  
  const displayMedName = isUnknownMed ? newForme : articleInfo?.colD;
  const displayMedUnit = isUnknownMed ? finalUnite : articleInfo?.colF;

  const isMl = useMemo(() => {
    if (!displayMedUnit) return false;
    return displayMedUnit.toLowerCase().includes('ml');
  }, [displayMedUnit]);

  const uniqueDatesDisplay = useMemo(() => {
    if (!restockData?.dates) return [];
    const seen = new Set();
    const result = [];
    restockData.dates.filter(d => !restockDeletedRows.includes(d.row)).forEach(d => {
      const key = `${d.lot}-${d.peremption}`;
      if (!seen.has(key)) { seen.add(key); result.push(d); }
    });
    return result;
  }, [restockData, restockDeletedRows]);

  const isScannedDateAlreadyKnown = useMemo(() => {
    if (!scannedMed || scannedMed.peremption === "Introuvable") return true;
    return uniqueDatesDisplay.some(d => d.peremption === scannedMed.peremption && (d.lot === scannedMed.lot || scannedMed.lot === "Introuvable"));
  }, [scannedMed, uniqueDatesDisplay]);

  // ------------ GESTION DU THÈME ------------
  const isRestock = modalType === 'restock';
  
  // Si c'est un nouveau médicament -> Vert (emerald). Sinon, si réappro -> Bleu. Sinon (conso) -> Rouge.
  const colorBase = isUnknownMed ? 'emerald' : (isRestock ? 'blue' : 'red');
  
  const headerBg = isUnknownMed 
    ? 'bg-emerald-600 text-white border-emerald-600' 
    : (isRestock ? 'bg-blue-700 text-white border-blue-700' : 'bg-red-700 text-white border-red-700');
    
  const highlightText = isUnknownMed ? 'text-emerald-800' : (isRestock ? 'text-blue-800' : 'text-red-800');
  const highlightNum = isUnknownMed ? 'text-emerald-600' : (isRestock ? 'text-blue-700' : 'text-red-700');
  
  const buttonPrimaryBg = isUnknownMed 
    ? 'bg-emerald-600 hover:bg-emerald-700' 
    : (isRestock ? 'bg-blue-700 hover:bg-blue-800' : 'bg-red-700 hover:bg-red-800');
  // ------------------------------------------

  const finalEmp = selectedEmp === 'Autre' ? newEmpAutre : selectedEmp;
  const currentStock = isUnknownMed ? 0 : (articleInfo?.emplacements?.find(e => e.emp === finalEmp)?.qty || 0);

  const startNewScan = () => {
    setScannedMed(null);
    setArticleInfo(null);
    setIsScannerOpen(true);
  };

  return (
    <>
      <PageLayout title="Scanner Médicament">
        {!isScannerOpen && scannedMed && (
          <div className="flex justify-center items-start mt-0 px-2 pb-8">
            <div className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden border border-slate-200">
              
              <div className={`shrink-0 p-4 flex items-center justify-between border-b-4 ${headerBg}`}>
                <div className="w-6"></div>
                <div className="font-bold text-lg uppercase tracking-wider text-center flex-1">
                  {isUnknownMed ? "Nouveau Produit" : (isRestock ? "Réapprovisionnement" : "Consommation")}
                </div>
                <div className="w-6 flex justify-end" title="État de la base">
                  <Database size={20} className={isDbLoaded ? "text-emerald-300" : dbLoadError ? "text-red-300" : "text-amber-300 animate-pulse"} />
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
                
                {modalStep === 'new_form' && (
                  <div className="animate-in fade-in duration-200">
                    <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 mb-6 shadow-sm text-left">
                      <p className="font-bold text-emerald-800 mb-2">Article non présent dans le catalogue</p>
                      <p className="text-xs text-emerald-600 mb-4 font-mono">CIP: {scannedMed.cip}</p>
                      
                      <div className="space-y-4 text-sm">
                        <div>
                          <label className="font-bold text-slate-700 block mb-1">Processus achat</label>
                          <select value={newProcessus} onChange={e => setNewProcessus(e.target.value)} className="w-full bg-white border border-slate-300 rounded p-2 focus:border-blue-500 outline-none">
                            <option value="">Sélectionner...</option>
                            <option value="Médicament">Médicament</option>
                            <option value="Antiseptique">Antiseptique</option>
                            <option value="Autre">Autre</option>
                          </select>
                          {newProcessus === 'Autre' && (
                            <input type="text" placeholder="Préciser..." value={newProcessusAutre} onChange={e => setNewProcessusAutre(e.target.value)} className="w-full border border-slate-300 rounded p-2 mt-2 focus:border-blue-500 outline-none" />
                          )}
                        </div>

                        <div>
                          <label className="font-bold text-slate-700 block mb-1">Forme présente dans l'inventaire</label>
                          <input type="text" placeholder="ex: Doliprane 1000mg comprimé" value={newForme} onChange={e => setNewForme(e.target.value)} className="w-full border border-slate-300 rounded p-2 focus:border-blue-500 outline-none" />
                        </div>

                        <div>
                          <label className="font-bold text-slate-700 block mb-1">Unité</label>
                          <select value={newUnite} onChange={e => setNewUnite(e.target.value)} className="w-full bg-white border border-slate-300 rounded p-2 focus:border-blue-500 outline-none">
                            <option value="">Sélectionner...</option>
                            {["Ampoule", "Bâton", "Comprimé", "Dose", "Flacon", "Gélule", "Lyophilisat", "mL", "Pansement", "Récipient unidose", "Sachet", "Seringue", "Stylo", "Suppositoire", "Tube", "Unité", "Autre"].map(u => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                          {newUnite === 'Autre' && (
                            <input type="text" placeholder="Préciser..." value={newUniteAutre} onChange={e => setNewUniteAutre(e.target.value)} className="w-full border border-slate-300 rounded p-2 mt-2 focus:border-blue-500 outline-none" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!isRestock && modalStep === 1 && !isUnknownMed && articleInfo?.found && (
                  <div className="animate-in fade-in zoom-in duration-200">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4 text-center shadow-sm">
                      <p className={`text-xl font-bold ${highlightText} leading-tight`}>
                        {displayMedName} <span className="text-slate-500 font-normal text-sm">({displayMedUnit})</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-2 font-mono">
                        Péremption : {scannedMed.peremption !== "Introuvable" ? scannedMed.peremption : "Non lue"}
                      </p>
                    </div>

                    <div className="mb-5 flex items-center justify-center gap-2 text-sm font-bold text-slate-700">
                      <span>Emplacement :</span>
                      <select 
                        value={selectedEmp}
                        onChange={(e) => setSelectedEmp(e.target.value)}
                        className={`bg-${colorBase}-50 hover:bg-${colorBase}-100 border-b-2 border-${colorBase}-400 text-${colorBase}-800 focus:border-${colorBase}-600 outline-none py-2.5 px-4 rounded-t-lg text-base font-bold cursor-pointer min-w-[150px] max-w-[220px] truncate transition-colors`}
                      >
                        {(isUnknownMed ? allEmplacements : (articleInfo?.emplacements?.map(e => e.emp) || [])).map((emp, idx) => (
                          <option key={idx} value={emp}>{emp || "Non défini"}</option>
                        ))}
                        {trigrammeEtab === 'AUT' && !((isUnknownMed ? allEmplacements : (articleInfo?.emplacements?.map(e => e.emp) || []))).includes('1F') && (
                          <option value="1F">1F</option>
                        )}
                      </select>
                    </div>

                    {(!isMl && !showCustomQty) ? (
                      <div>
                        <p className="text-sm font-bold text-slate-700 mb-2 text-center">Quantité consommée (en {displayMedUnit}):</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[1, 2, 3].map(num => (
                            <button
                              key={num}
                              onClick={() => { setSelectedQty(num); setModalStep(2); }}
                              className={`bg-${colorBase}-50 border-2 border-${colorBase}-100 hover:bg-${colorBase}-100 ${highlightNum} font-black text-xl py-2 rounded-xl transition active:scale-95 shadow-sm`}
                            >
                              {num}
                            </button>
                          ))}
                          <button
                            onClick={() => setShowCustomQty(true)}
                            className={`col-span-3 bg-white border-2 border-${colorBase}-200 text-${colorBase}-600 font-bold text-lg py-2 rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm`}
                          >
                            Autre quantité
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 animate-in fade-in duration-200">
                        <p className="text-sm font-bold text-slate-700 mb-1 text-center">Saisir la quantité :</p>
                        <input 
                          type="number" 
                          min="1"
                          placeholder={isMl ? "Quantité en mL (ex: 15)" : "Ex: 5"}
                          value={customQtyValue}
                          onChange={(e) => setCustomQtyValue(e.target.value)}
                          className={`w-full border-2 border-${colorBase}-300 rounded-xl p-4 text-center text-2xl font-bold focus:border-${colorBase}-500 focus:ring-0`}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                )}

                {isRestock && modalStep === 1 && (
                  <div className="animate-in fade-in duration-200">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4 text-center shadow-sm">
                      <p className={`text-xl font-bold ${highlightText} leading-tight`}>
                        {displayMedName}
                      </p>
                      <p className="text-sm text-slate-600 mt-2 font-mono bg-white border border-slate-200 rounded py-1 px-3 inline-block">
                        Stock : <span className="font-bold text-slate-800">{currentStock}</span> {isMl ? "mL" : ` ${displayMedUnit || ""}`}(s)
                      </p>
                    </div>

                    <div className="mb-5 flex flex-col items-center justify-center gap-2 text-sm font-bold text-slate-700 w-full">
                      <div className="flex items-center gap-2 w-full justify-center">
                        <span className="shrink-0">Emplacement :</span>
                        <select 
                          value={selectedEmp}
                          onChange={(e) => setSelectedEmp(e.target.value)}
                          className={`bg-${colorBase}-50 hover:bg-${colorBase}-100 border-b-2 border-${colorBase}-400 text-${colorBase}-800 focus:border-${colorBase}-600 outline-none py-2.5 px-4 rounded-t-lg text-base font-bold cursor-pointer min-w-[150px] max-w-[220px] truncate transition-colors`}
                        >
                          {allEmplacements.map((emp, idx) => (
                            <option key={idx} value={emp}>{emp || "Non défini"}</option>
                          ))}
                          {trigrammeEtab === 'AUT' && !allEmplacements.includes('1F') && (
                            <option value="1F">1F</option>
                          )}
                          <option value="Autre">Autre...</option>
                        </select>
                      </div>
                      {selectedEmp === 'Autre' && (
                        <input 
                          type="text" 
                          placeholder="Préciser l'emplacement..." 
                          value={newEmpAutre} 
                          onChange={(e) => setNewEmpAutre(e.target.value)} 
                          className={`mt-1 w-full max-w-[200px] border border-${colorBase}-300 rounded p-1.5 text-center focus:border-${colorBase}-500 outline-none font-normal`} 
                          autoFocus
                        />
                      )}
                    </div>

                    <div className="mb-6 flex flex-row items-center justify-center gap-3">
                      <p className="text-sm font-bold text-slate-700">
                        Quantité à rajouter (en {displayMedUnit}) :
                      </p>
                      <input 
                        type="number" 
                        min="1"
                        value={restockAddedQty}
                        onChange={(e) => setRestockAddedQty(e.target.value)}
                        className="w-32 border-2 border-blue-300 rounded-xl p-3 text-center text-2xl font-bold focus:border-blue-500 focus:ring-0"
                      />
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-bold text-slate-700 mb-2">Dates de péremption existantes :</p>
                      
                      <div className="space-y-2">
                        {uniqueDatesDisplay.map(d => (
                          <div key={d.row} className="flex justify-between items-center bg-white border border-slate-200 p-2 rounded-lg">
                            <div>
                              <span className="font-mono text-sm font-bold text-slate-700">{d.peremption}</span>
                              {d.lot && <span className="text-xs text-slate-500 ml-2">Lot: {d.lot}</span>}
                            </div>
                            <button onClick={() => setRestockDeletedRows([...restockDeletedRows, d.row])} className="text-red-500 hover:bg-red-50 p-1 rounded transition">
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                        
                        {uniqueDatesDisplay.length === 0 && (
                          <p className="text-xs text-slate-400 italic">Aucune date enregistrée</p>
                        )}

                        {scannedMed.peremption !== "Introuvable" && !isScannedDateAlreadyKnown && (
                          <div className="flex justify-between items-center bg-blue-50 border border-blue-300 p-2 rounded-lg mt-2">
                            <div className="flex flex-col">
                              <span className="font-mono text-sm font-bold text-blue-800">{scannedMed.peremption}</span>
                              {scannedMed.lot && scannedMed.lot !== "Introuvable" && <span className="text-xs text-blue-600">Lot: {scannedMed.lot}</span>}
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-200 text-blue-800 px-2 py-0.5 rounded">Nouveau</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {modalStep === 2 && (
                  <div className="animate-in fade-in slide-in-from-right-4 duration-200 text-center py-2">
                    <p className="text-slate-500 text-sm font-bold uppercase tracking-widest mb-4">Résumé</p>
                    
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-800 leading-tight mb-2">
                        {displayMedName} <span className="text-slate-500 font-normal text-sm"> ({finalEmp || "Non défini"})</span>
                      </p>

                      <div className={`bg-${colorBase}-50 rounded-2xl p-3 border border-${colorBase}-200 shadow-inner relative overflow-hidden mb-6`}>
                        <p className={`text-4xl font-black ${highlightNum} relative z-10`}>
                          {isRestock ? "+" : "-"} {isRestock ? restockAddedQty : selectedQty} {isMl ? "mL" : ` ${displayMedUnit || ""}`}(s)
                        </p>
                        {isRestock && (
                          <p className="text-base font-semibold text-slate-600 mt-3 relative z-10">
                            Nouveau Total : <span className="text-blue-700 text-xl">{Number(currentStock) + Number(restockAddedQty)}</span> {isMl ? "mL" : ""}
                          </p>
                        )}
                      </div>
                      
                      {isRestock && (
                        <div className="text-left text-sm bg-slate-100 p-4 rounded-xl border border-slate-200">
                          <span className="font-semibold text-slate-700 block mb-2">Péremptions valides :</span>
                          {uniqueDatesDisplay.map(d => (
                            <span key={d.row} className="inline-block bg-white border px-2 py-1 rounded-md mr-2 mb-2 shadow-sm font-mono text-slate-700">{d.peremption}</span>
                          ))}
                          {scannedMed.peremption !== "Introuvable" && !isScannedDateAlreadyKnown && (
                            <span className="inline-block bg-blue-100 border border-blue-300 text-blue-800 font-bold px-2 py-1 rounded-md mr-2 mb-2 shadow-sm font-mono">{scannedMed.peremption}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="shrink-0 p-4 bg-slate-50 border-t shadow-inner">
                
                {modalStep === 'new_form' && (
                  <div className="flex gap-2">
                    <button onClick={() => navigate('/arrivee')} className="flex-1 bg-white border border-slate-300 text-slate-700 rounded-xl py-4 font-bold hover:bg-slate-50 transition">
                      Annuler
                    </button>
                    <button
                      disabled={!finalProcessus || !newForme || !finalUnite}
                      onClick={() => {
                        setRestockData({ dates: [] });
                        setRestockDeletedRows([]);
                        setRestockAddedQty(1);
                        setModalStep(1);
                      }}
                      className={`flex-[2] ${buttonPrimaryBg} disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-xl py-4 font-bold text-lg transition shadow-md`}
                    >
                      Suivant
                    </button>
                  </div>
                )}

                {!isRestock && modalStep === 1 && !showCustomQty && !isMl && (
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate('/arrivee')}
                        className="flex-1 bg-white border border-slate-300 text-slate-700 rounded-xl py-4 font-bold hover:bg-slate-50 transition"
                      >
                        Fermer
                      </button>
                      <button
                        onClick={startNewScan}
                        className={`${buttonPrimaryBg} flex-1 text-white rounded-xl py-4 font-bold transition shadow-md`}
                      >
                        Nouveau scan
                      </button>
                    </div>
                    <button
                      onClick={handleOpenRestock}
                      className="w-full bg-blue-100 text-blue-800 border-2 border-blue-200 rounded-xl py-3.5 font-bold hover:bg-blue-200 transition flex items-center justify-center gap-2"
                    >
                      <PackagePlus size={20} /> Réapprovisionnement
                    </button>
                  </div>
                )}

                {((!isRestock && modalStep === 1 && (showCustomQty || isMl)) || (isRestock && modalStep === 1)) && !isUnknownMed && (
                  <div className={`flex flex-col ${isMl ? 'gap-2' : 'gap-3'}`}>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (isRestock) setModalType('consume');
                          else if (isMl) startNewScan();
                          else setShowCustomQty(false);
                        }}
                        className={`flex-1 bg-white border border-slate-300 text-slate-700 rounded-xl ${isMl ? 'py-2.5' : 'py-4'} font-bold hover:bg-slate-50 transition`}
                      >
                        {(!isRestock && isMl) ? 'Nouveau scan' : 'Retour'}
                      </button>
                      <button
                        disabled={isRestock ? (!restockAddedQty || restockAddedQty <= 0) : (!customQtyValue || customQtyValue <= 0)}
                        onClick={() => {
                          if (!isRestock) setSelectedQty(Number(customQtyValue));
                          setModalStep(2);
                        }}
                        className={`flex-[2] ${buttonPrimaryBg} disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold ${isMl ? 'py-2.5' : 'py-4'} rounded-xl text-lg transition shadow-md`}
                      >
                        Suivant
                      </button>
                    </div>
                    {!isRestock && isMl && (
                      <button
                        onClick={handleOpenRestock}
                        className="w-full bg-blue-100 text-blue-800 border-2 border-blue-200 rounded-xl py-2.5 font-bold hover:bg-blue-200 transition flex items-center justify-center gap-2"
                      >
                        <PackagePlus size={20} /> Réapprovisionnement
                      </button>
                    )}
                  </div>
                )}
                
                {isRestock && modalStep === 1 && isUnknownMed && (
                  <div className="flex gap-2">
                    <button onClick={() => setModalStep('new_form')} className="flex-1 bg-white border border-slate-300 text-slate-700 rounded-xl py-4 font-bold hover:bg-slate-50 transition">
                      Retour
                    </button>
                    <button
                      disabled={!restockAddedQty || restockAddedQty <= 0}
                      onClick={() => setModalStep(2)}
                      className={`flex-[2] ${buttonPrimaryBg} disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold py-4 rounded-xl text-lg transition shadow-md`}
                    >
                      Suivant
                    </button>
                  </div>
                )}

                {modalStep === 2 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setModalStep(1);
                        if (!isRestock && !isMl) setShowCustomQty(false); 
                      }}
                      className="flex-1 bg-white border border-slate-300 text-slate-700 rounded-xl py-4 font-bold hover:bg-slate-50 transition"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => {
                        const params = new URLSearchParams({
                          action: isRestock ? "restock" : "consume",
                          cip: scannedMed.cip || "",
                          site: trigrammeEtab,
                          quantite: isRestock ? restockAddedQty : selectedQty,
                          emplacement: finalEmp,
                          is_new: isUnknownMed ? 'true' : 'false',
                          new_id: scannedMed.gtin || scannedMed.cip,
                          processus: finalProcessus,
                          forme: newForme,
                          unite: finalUnite
                        });

                        if (isRestock) {
                          params.append("deleted_rows", JSON.stringify(restockDeletedRows));
                          params.append("new_peremption", scannedMed.peremption !== "Introuvable" ? scannedMed.peremption : "");
                          params.append("new_lot", scannedMed.lot !== "Introuvable" ? scannedMed.lot : "");
                        }

                        if (globalMedicamentsCache) {
                          if (isUnknownMed) {
                            globalMedicamentsCache.medicaments[scannedMed.cip] = {
                              colD: newForme, colE: '', colF: finalUnite,
                              qty: Number(restockAddedQty),
                              emplacements: [{ emp: finalEmp, qty: Number(restockAddedQty) }],
                              dates: []
                            };
                            if (scannedMed.peremption !== "Introuvable") {
                              globalMedicamentsCache.medicaments[scannedMed.cip].dates.push({ row: Date.now(), lot: scannedMed.lot, peremption: scannedMed.peremption });
                            }
                          } else if (globalMedicamentsCache.medicaments[scannedMed.cip]) {
                            const cachedMed = globalMedicamentsCache.medicaments[scannedMed.cip];
                            const empIdx = cachedMed.emplacements?.findIndex(e => e.emp === finalEmp);
                            
                            if (isRestock) {
                              if (empIdx !== -1 && cachedMed.emplacements) cachedMed.emplacements[empIdx].qty += Number(restockAddedQty);
                              else cachedMed.emplacements.push({ emp: finalEmp, qty: Number(restockAddedQty) });
                              cachedMed.qty += Number(restockAddedQty);
                              cachedMed.dates = cachedMed.dates.filter(d => !restockDeletedRows.includes(d.row));
                              if (scannedMed.peremption !== "Introuvable") {
                                cachedMed.dates.push({ row: Date.now(), lot: scannedMed.lot, peremption: scannedMed.peremption });
                              }
                            } else {
                              if (empIdx !== -1 && cachedMed.emplacements) cachedMed.emplacements[empIdx].qty -= Number(selectedQty);
                              cachedMed.qty -= Number(selectedQty);
                            }
                          }
                        }

                        fetchJSONP(`${SCRIPT_URL}?${params.toString()}&t=${Date.now()}`)
                          .then(response => {
                            if (response && response.error) toast.error(`❌ Échec : ${response.error}`, { duration: 6000 });
                          }).catch(() => {});

                        toast.success(isRestock ? "✅ Réapprovisionnement enregistré !" : "✅ Consommation enregistrée !");
                        setScannedMed(null);
                        setArticleInfo(null);
                        setModalStep(0);
                        
                        setTimeout(() => navigate('/arrivee'), 1000);
                      }}
                      className={`flex-[2] ${buttonPrimaryBg} text-white rounded-xl py-4 font-bold transition shadow-md text-lg active:scale-95 flex justify-center items-center gap-2`}
                    >
                      <Check size={24} /> Valider
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </PageLayout>

      {isScannerOpen && (
        <BarcodeScanner 
          isOpen={true} 
          onClose={() => navigate('/arrivee')} 
          onScan={handleScan} 
        />
      )}
    </>
  );
}